import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../auth/AuthContext";

const SHOP_ROLES = ["manager", "cashier", "viewer"];
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const EMPTY_SHOP_FORM = { name: "", code: "", address: "", phone: "", timezone: "", currency: "" };
const EMPTY_USER_FORM = {
  email: "",
  password: "",
  full_name: "",
  is_org_admin: false,
  is_active: true,
  shop_id: "",
  shop_role: "manager",
};

export default function ShopsUsersPage() {
  const { user: currentUser, activeShop } = useAuth();

  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [shopForm, setShopForm] = useState(EMPTY_SHOP_FORM);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [editUserId, setEditUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(""), 6000);
      return () => clearTimeout(t);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(""), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [shopsRes, usersRes] = await Promise.all([api.get("/shops"), api.get("/users")]);
      setShops(shopsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError("Failed to load shops/users");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleShopChange = (e) => setShopForm({ ...shopForm, [e.target.name]: e.target.value });
  const handleUserChange = (e) => {
    const { name, type, value, checked } = e.target;
    setUserForm({ ...userForm, [name]: type === "checkbox" ? checked : value });
  };

  const handleCreateShop = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    try {
      const res = await api.post("/shops", {
        name: shopForm.name,
        code: shopForm.code || null,
        address: shopForm.address || null,
        phone: shopForm.phone || null,
        timezone: shopForm.timezone || "UTC",
        currency: shopForm.currency || activeShop?.currency || "USD",
      });
      setMessage(`Shop "${res.data.name}" created.`);
      setShopForm(EMPTY_SHOP_FORM);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create shop");
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    try {
      const shopRoles =
        !userForm.is_org_admin && userForm.shop_id
          ? [{ shop_id: Number(userForm.shop_id), role: userForm.shop_role }]
          : [];

      if (editUserId) {
        const res = await api.patch(`/users/${editUserId}`, {
          email: userForm.email,
          full_name: userForm.full_name || null,
          org_role: userForm.is_org_admin ? "org_admin" : null,
          is_active: userForm.is_active,
          shop_roles: shopRoles,
        });
        setMessage(`User "${res.data.email}" updated.`);
      } else {
        const res = await api.post("/users", {
          email: userForm.email,
          password: userForm.password,
          full_name: userForm.full_name || null,
          org_role: userForm.is_org_admin ? "org_admin" : null,
          shop_roles: shopRoles,
        });
        setMessage(`User "${res.data.email}" created.`);
      }
      setUserForm(EMPTY_USER_FORM);
      setEditUserId(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEditUser = (u) => {
    setMessage("");
    setError("");
    setEditUserId(u.id);
    setUserForm({
      email: u.email,
      password: "",
      full_name: u.full_name || "",
      is_org_admin: u.org_role === "org_admin",
      is_active: u.is_active,
      shop_id: u.shop_roles[0]?.shop_id ?? "",
      shop_role: u.shop_roles[0]?.role ?? "manager",
    });
  };

  const handleCancelUserEdit = () => {
    setEditUserId(null);
    setUserForm(EMPTY_USER_FORM);
    setError("");
  };

  return (
    <div className="content-grid">
      <div className="card form-card">
        <h2>Add Shop</h2>
        <form onSubmit={handleCreateShop} className="product-form labeled-form">
          <label className="field-label">
            Shop name
            <input type="text" name="name" value={shopForm.name} onChange={handleShopChange} required />
          </label>
          <label className="field-label">
            Code (optional)
            <input type="text" name="code" value={shopForm.code} onChange={handleShopChange} />
          </label>
          <label className="field-label">
            Address (optional)
            <input type="text" name="address" value={shopForm.address} onChange={handleShopChange} />
          </label>
          <label className="field-label">
            Phone (optional)
            <input type="text" name="phone" value={shopForm.phone} onChange={handleShopChange} />
          </label>
          <label className="field-label">
            Timezone
            <input
              type="text"
              name="timezone"
              placeholder="e.g. Africa/Kampala (defaults to UTC)"
              value={shopForm.timezone}
              onChange={handleShopChange}
            />
          </label>
          <label className="field-label">
            Currency code
            <input
              type="text"
              name="currency"
              placeholder={`e.g. UGX (defaults to ${activeShop?.currency || "USD"})`}
              value={shopForm.currency}
              onChange={handleShopChange}
            />
          </label>
          <div className="form-actions">
            <button className="btn" type="submit">
              Create Shop
            </button>
          </div>
        </form>
      </div>

      <div className="card list-card">
        <h2>Shops</h2>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <div className="scroll-x">
            <table className="product-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Currency</th>
                  <th>Timezone</th>
                  <th>Your Role</th>
                </tr>
              </thead>
              <tbody>
                {shops.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id}</td>
                    <td className="name-cell">{s.name}</td>
                    <td>{s.currency}</td>
                    <td>{s.timezone}</td>
                    <td>{s.role}</td>
                  </tr>
                ))}
                {shops.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      No shops yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card form-card">
        <h2>{editUserId ? "Edit User" : "Add User"}</h2>
        <form onSubmit={handleUserSubmit} className="product-form labeled-form">
          <label className="field-label">
            Email
            <input type="email" name="email" value={userForm.email} onChange={handleUserChange} required />
          </label>
          {!editUserId && (
            <label className="field-label">
              Password
              <input
                type="password"
                name="password"
                value={userForm.password}
                onChange={handleUserChange}
                required
              />
            </label>
          )}
          <label className="field-label">
            Full name (optional)
            <input type="text" name="full_name" value={userForm.full_name} onChange={handleUserChange} />
          </label>

          <label className="checkbox-label">
            <input type="checkbox" name="is_org_admin" checked={userForm.is_org_admin} onChange={handleUserChange} />
            Organization Admin - full access to every shop in the org
          </label>

          {editUserId && editUserId !== currentUser?.id && (
            <label className="checkbox-label">
              <input type="checkbox" name="is_active" checked={userForm.is_active} onChange={handleUserChange} />
              Active - unchecking blocks this user from logging in
            </label>
          )}

          {!userForm.is_org_admin && (
            <>
              <label className="field-label">
                Shop to assign
                <select name="shop_id" value={userForm.shop_id} onChange={handleUserChange} required>
                  <option value="">Select a shop...</option>
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Role at that shop
                <select name="shop_role" value={userForm.shop_role} onChange={handleUserChange}>
                  {SHOP_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {capitalize(r)}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          <div className="form-actions">
            <button className="btn" type="submit">
              {editUserId ? "Update User" : "Create User"}
            </button>
            {editUserId && (
              <button className="btn btn-secondary" type="button" onClick={handleCancelUserEdit}>
                Cancel
              </button>
            )}
          </div>
        </form>
        {message && <div className="success-msg">{message}</div>}
        {error && <div className="error-msg">{error}</div>}
      </div>

      <div className="card list-card">
        <h2>Users</h2>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <div className="scroll-x">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td className="name-cell">{u.full_name || "-"}</td>
                    <td>
                      {u.org_role === "org_admin" ? (
                        <span className="chip">Org Admin (all shops)</span>
                      ) : u.shop_roles.length === 0 ? (
                        <span className="muted">No shop access</span>
                      ) : (
                        u.shop_roles.map((sr) => (
                          <span key={sr.shop_id} className="chip" style={{ marginRight: 6 }}>
                            {sr.shop_name}: {capitalize(sr.role)}
                          </span>
                        ))
                      )}
                    </td>
                    <td>
                      {u.is_active ? (
                        <span className="muted">Active</span>
                      ) : (
                        <span className="chip">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-edit" onClick={() => handleEditUser(u)}>
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      No users yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
